import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, BookPlus, Search } from 'lucide-react';
import type { EntityPreview } from '@nw/contracts';
import { Button, DottedRing, Instrument, Readout, TickBars } from '@nw/ui';
import { ApiError, api, useFormat, useHome, verbMatcher, type AssistantCommand, type WidgetDef } from '@nw/web-platform';
import { spineFor, STATUS_LABELS, type Book, type LibraryStats, type ResumeReading } from '../public/index.js';
import { BookCover } from './parts.js';

async function findBook(q: string) {
  const res = await api<EntityPreview[]>(`/search?q=${encodeURIComponent(q)}&types=library:book&limit=5`);
  return res.data[0] ?? null;
}

export const libraryCommands: AssistantCommand[] = [
  {
    id: 'library.add',
    title: 'Add a book',
    usage: 'add book <title>',
    icon: BookPlus,
    needsArg: true,
    match: verbMatcher(['add book', 'new book', 'add a book'], {
      argRequired: true,
    }),
    run: async (arg) => {
      try {
        const book = (
          await api<Book>('/library/books', {
            body: { title: arg!.slice(0, 300), status: 'want_to_read' },
          })
        ).data;
        return {
          ok: true,
          message: `Added “${book.title}” to your library`,
          href: `/library/books/${book.id}`,
        };
      } catch (err) {
        return {
          ok: false,
          message: err instanceof ApiError ? err.message : 'The book could not be added',
        };
      }
    },
  },
  {
    id: 'library.read',
    title: 'Read a book',
    usage: 'read <title>, or “continue reading”',
    icon: BookOpen,
    match: (text) => {
      const m = verbMatcher(['continue reading', 'resume reading', 'read', 'open book'])(text);
      return m;
    },
    run: async (arg, ctx) => {
      if (arg) {
        const hit = await findBook(arg);
        if (!hit)
          return {
            ok: false,
            message: `No book in your library matches “${arg}”.`,
          };
        ctx.navigate(`/library/books/${hit.ref.entityId}/read`);
        return { ok: true, message: `Opening ${hit.title}` };
      }
      const home = (
        await api<{
          sections: Array<{ moduleId: string; kind: string; data: unknown }>;
        }>('/home')
      ).data;
      const resume = home.sections.find((s) => s.moduleId === 'library' && s.kind === 'resume')?.data as ResumeReading | null;
      if (!resume)
        return {
          ok: false,
          message: 'Nothing is in progress. Say “read” and a title.',
        };
      ctx.navigate(`/library/books/${resume.book.id}/read`);
      return { ok: true, message: `Back to ${resume.book.title}` };
    },
  },
  {
    id: 'library.find',
    title: 'Find in the library',
    usage: 'find book <title or author>',
    icon: Search,
    needsArg: true,
    match: verbMatcher(['find book', 'find books', 'books by', 'library search'], { argRequired: true }),
    run: async (arg, ctx) => {
      ctx.navigate(`/library?q=${encodeURIComponent(arg!)}`);
      return { ok: true, message: `Books matching “${arg}”` };
    },
  },
];

function useSection<T>(kind: string) {
  const home = useHome();
  return {
    data: home.data?.sections.find((s) => s.moduleId === 'library' && s.kind === kind)?.data as T | undefined,
    loading: home.isPending,
  };
}

function ReadingWidget({ size }: { size: string }) {
  const { data } = useSection<ResumeReading | null>('resume');
  const fmt = useFormat();
  if (!data) {
    return (
      <Instrument label="Reading">
        <p className="mt-auto text-sm text-[var(--nw-quiet)]">Nothing in progress. Say “add book” and a title, or open the Library.</p>
      </Instrument>
    );
  }
  const b = data.book;
  const spine = spineFor(b.id, b.title, b.pageCount);
  const page = data.currentPage ?? 0;
  return (
    <Instrument
      label="Reading"
      status={
        <>
          <span className="nw-live-dot" /> {data.lastReadAt ? fmt.relative(data.lastReadAt) : 'Not started'}
        </>
      }
    >
      <div className={size === 'l' ? 'mt-3 flex min-h-0 flex-1 gap-5' : 'mt-2 flex min-h-0 flex-1 gap-4'}>
        {size === 'l' ? (
          <BookCover
            layout={false}
            className="w-28 shrink-0 self-start"
            book={{
              id: b.id,
              title: b.title,
              authors: b.authorNames.map((n) => ({ personId: n, name: n })),
              cover: {
                kind: b.coverUrl ? 'openlibrary' : 'none',
                url: b.coverUrl,
              },
              spine,
            }}
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="font-display line-clamp-2 text-xl leading-tight">{b.title}</p>
          <p className="truncate text-sm text-[var(--nw-quiet)]">{b.authorNames.join(', ')}</p>
          <div className="mt-auto">
            <Readout value={data.currentPage ?? '—'} unit={b.pageCount ? `of ${b.pageCount} pages` : 'page, total unknown'} />
            {b.pageCount ? <TickBars value={page} max={b.pageCount} label={`Reading progress, page ${page} of ${b.pageCount}`} /> : null}
            <Button asChild variant="primary" size="sm" className="mt-3">
              <Link to="/library/books/$bookId/read" params={{ bookId: b.id }}>
                <BookOpen /> Resume
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Instrument>
  );
}

const STATUS_COLOURS: Record<string, string> = {
  reading: 'var(--nw-live)',
  want_to_read: 'var(--nw-river)',
  paused: '#e3a55c',
  finished: 'var(--nw-ink)',
};

function ShelfWidget() {
  const stats = useQuery({
    queryKey: ['library', 'stats'],
    queryFn: async () => (await api<LibraryStats>('/library/stats')).data,
  });
  const s = stats.data;
  const order = ['reading', 'want_to_read', 'paused', 'finished'] as const;
  return (
    <Instrument
      label="Library"
      action={
        <Link to="/library" className="text-xs text-[var(--nw-quiet)] hover:underline">
          Open
        </Link>
      }
    >
      <div className="mt-2 flex flex-1 items-center gap-4">
        <DottedRing
          size={108}
          segments={order.map((k) => ({
            value: s?.byStatus[k] ?? 0,
            color: STATUS_COLOURS[k]!,
            label: STATUS_LABELS[k],
          }))}
          centre={
            <span>
              <span className="nw-readout block text-3xl">{s?.total ?? '—'}</span>
              <span className="text-[10px] text-[var(--nw-quiet)]">books</span>
            </span>
          }
        />
        <ul className="nw-ring-legend grid gap-1 text-xs">
          {order.map((k) => (
            <li key={k} className="flex items-center gap-2">
              <span className="size-2 rounded-full" style={{ background: STATUS_COLOURS[k] }} />
              <span className="text-[var(--nw-quiet)]">{STATUS_LABELS[k]}</span>
              <span className="ms-auto font-semibold tabular-nums">{s?.byStatus[k] ?? 0}</span>
            </li>
          ))}
        </ul>
      </div>
    </Instrument>
  );
}

function NoteWidget() {
  const { data } = useSection<
    Array<{
      id: string;
      excerpt: string;
      bookId: string;
      bookTitle: string;
      page: number | null;
      updatedAt: string;
    }>
  >('recentNotes');
  const fmt = useFormat();
  const n = data?.[0];
  return (
    <Instrument label="From your reading notes">
      {n ? (
        <>
          <p className="font-display mt-2 line-clamp-3 text-[1.15rem] leading-snug">{n.excerpt}</p>
          <Link to="/library/books/$bookId" params={{ bookId: n.bookId }} className="mt-auto text-xs text-[var(--nw-quiet)] hover:underline">
            {n.bookTitle}
            {n.page ? `, p.${n.page}` : ''} · {fmt.relative(n.updatedAt)}
          </Link>
        </>
      ) : (
        <p className="mt-auto text-sm text-[var(--nw-quiet)]">Notes you write while reading appear here.</p>
      )}
    </Instrument>
  );
}

export const libraryWidgets: WidgetDef[] = [
  {
    id: 'library.reading',
    title: 'Reading',
    description: 'The book you are in the middle of, with progress',
    sizes: ['m', 'l'],
    defaultSize: 'l',
    Component: ReadingWidget,
  },
  {
    id: 'library.shelf',
    title: 'Library dial',
    description: 'Books by reading status',
    sizes: ['s', 'm'],
    defaultSize: 's',
    Component: ShelfWidget,
  },
  {
    id: 'library.note',
    title: 'Latest reading note',
    description: 'The last thing you wrote in a book',
    sizes: ['m'],
    defaultSize: 'm',
    Component: NoteWidget,
  },
];

/** One live line for the Spaces grid and widget. */
export function useLibraryStatus(): string | null {
  const stats = useQuery({
    queryKey: ['library', 'stats'],
    queryFn: async () => (await api<LibraryStats>('/library/stats')).data,
  });
  const s = stats.data;
  if (!s) return null;
  const reading = s.byStatus.reading ?? 0;
  return `${s.total} ${s.total === 1 ? 'book' : 'books'}${reading ? ` · ${reading} reading` : ''}`;
}
